/**
 * The two things a vehicle placard needs that neither fleet owns: the LAYER that lays a
 * flat icon on the vehicle's own plane, and the DOT-MATRIX display technology every route
 * sign on this map is stamped through.
 *
 * Both were `r160Model.ts`'s private code until the Xcelsior grew its three destination
 * signs. An MTA bus and an R160 carry the *same product* — an amber LED matrix behind
 * glass — so this is one technology serving two fleets, and it lives once here for the
 * same reason `trainConsist.ts` exists: a formula that two callers share must not be a
 * formula that two callers each have a copy of.
 *
 * What is NOT here is any position, size or content: where a sign sits and what it says
 * are per-body facts, and they stay in `r160Model.ts` and `xcel40Model.ts` beside the
 * measurements they were derived from.
 */
import type { Accessor } from '@deck.gl/core'
import { IconLayer } from '@deck.gl/layers'

/** A drawn placard, in the shape deck's `getIcon` wants back. */
export type SignPlacard = { url: string; width: number; height: number }

type VehicleSideIconProps<DataT> = {
  getBearing: Accessor<DataT, number>
  getSide: Accessor<DataT, number>
  getFacing: Accessor<DataT, number>
}

// IconLayer is normally a screen-facing billboard. This shader lays dynamic vehicle
// placards on a vertical plane whose horizontal axis follows the vehicle instead —
// ALONG it for a sidewall decal (facing 0), ACROSS it for one on an end face
// (facing 1), which is the only plane a cab-front flag or a destination sign can
// sit on.
//
// `side` is the reading direction, and it is not free: the icon's own left-to-right
// runs along `-side * plane`. On a sidewall that means side +1 for the vehicle's LEFT
// flank (where forward is to the viewer's left, so text reads toward the rear) and −1
// for its RIGHT; on an end face it means +1 at the FRONT and −1 at the REAR. Both
// fleets' placement tables are written to that rule.
const VEHICLE_SIDE_ICON_VERTEX_SHADER = `\
#version 300 es
#define SHADER_NAME vehicle-side-icon-layer-vertex-shader

in vec2 positions;

in vec3 instancePositions;
in vec3 instancePositions64Low;
in float instanceSizes;
in float instanceAngles;
in vec4 instanceColors;
in vec3 instancePickingColors;
in vec4 instanceIconFrames;
in float instanceColorModes;
in vec2 instanceOffsets;
in vec2 instancePixelOffset;
in float instanceBearings;
in float instanceSides;
in float instanceFacings;

out float vColorMode;
out vec4 vColor;
out vec2 vTextureCoords;
out vec2 uv;

vec2 rotate_by_angle(vec2 vertex, float angle) {
  float angle_radian = angle * PI / 180.0;
  float cos_angle = cos(angle_radian);
  float sin_angle = sin(angle_radian);
  mat2 rotationMatrix = mat2(cos_angle, -sin_angle, sin_angle, cos_angle);
  return rotationMatrix * vertex;
}

void main(void) {
  geometry.worldPosition = instancePositions;
  geometry.uv = positions;
  geometry.pickingColor = instancePickingColors;
  uv = positions;

  vec2 iconSize = instanceIconFrames.zw;
  float sizePixels = clamp(
    project_size_to_pixel(instanceSizes * icon.sizeScale, icon.sizeUnits),
    icon.sizeMinPixels, icon.sizeMaxPixels
  );
  float iconConstraint = icon.sizeBasis == 0.0 ? iconSize.x : iconSize.y;
  float instanceScale = iconConstraint == 0.0 ? 0.0 : sizePixels / iconConstraint;

  vec2 pixelOffset = positions / 2.0 * iconSize + instanceOffsets;
  pixelOffset = rotate_by_angle(pixelOffset, instanceAngles) * instanceScale;
  pixelOffset += instancePixelOffset;
  pixelOffset.y *= -1.0;

  float bearing = instanceBearings * PI / 180.0;
  vec2 forward = vec2(sin(bearing), cos(bearing));
  // Forward turned a quarter-turn right — the vehicle's starboard direction, which is
  // the axis an end-face placard reads along.
  vec2 across = vec2(cos(bearing), -sin(bearing));
  vec2 plane = mix(forward, across, instanceFacings);
  vec2 offsetSize = project_pixel_size(pixelOffset);
  vec3 offsetCommon = vec3(-instanceSides * plane * offsetSize.x, offsetSize.y);
  DECKGL_FILTER_SIZE(offsetCommon, geometry);
  gl_Position = project_position_to_clipspace(
    instancePositions, instancePositions64Low, offsetCommon, geometry.position
  );
  DECKGL_FILTER_GL_POSITION(gl_Position, geometry);

  vTextureCoords = mix(
    instanceIconFrames.xy,
    instanceIconFrames.xy + iconSize,
    (positions.xy + 1.0) / 2.0
  ) / icon.iconsTextureDim;

  vColor = instanceColors;
  DECKGL_FILTER_COLOR(vColor, geometry);
  vColorMode = instanceColorModes;
}
`

export class VehicleSideIconLayer<DataT = any> extends IconLayer<
  DataT,
  VehicleSideIconProps<DataT>
> {
  static defaultProps = {
    ...IconLayer.defaultProps,
    getBearing: { type: 'accessor', value: 0 },
    getSide: { type: 'accessor', value: 1 },
    getFacing: { type: 'accessor', value: 0 },
  }
  static layerName = 'VehicleSideIconLayer'

  initializeState() {
    super.initializeState()
    this.getAttributeManager()!.addInstanced({
      instanceBearings: { size: 1, accessor: 'getBearing' },
      instanceSides: { size: 1, accessor: 'getSide' },
      instanceFacings: { size: 1, accessor: 'getFacing' },
    })
  }

  getShaders() {
    return { ...super.getShaders(), vs: VEHICLE_SIDE_ICON_VERTEX_SHADER }
  }
}

// ── The dot matrix ───────────────────────────────────────────────────────────────────

/** The lit colour of both fleets' destination signs. Amber, as the real displays are. */
export const SIGN_AMBER = '#ffb11b'
/** The display's own dark ground: unlit glass, not the vehicle's paint. */
export const SIGN_GROUND = '#080704'

/**
 * The face a route sign is stamped through, as a canvas `ctx.font`.
 *
 * Deliberately NOT the app's bullet face (`ui/theme.ts`'s Helvetica clone): these are
 * dot-matrix displays, and the real ones run a condensed face so a destination fits the
 * strip. The mask it is drawn into is then resampled onto the LED grid, so what survives
 * is the letterform's proportion, which is the whole reason to condense. One definition
 * because every sign on the map — the R160's side signs and front bullet, the bus's
 * front, side and rear — must be the same type.
 */
export function signFont(px: number): string {
  return `bold ${px}px "Arial Narrow", "Roboto Condensed", sans-serif`
}

/**
 * The unlit LED field: every dot of the grid, dark, before anything is stamped lit
 * over it. A display reads as a display because its dead pixels are visible too —
 * without this the sign is glowing text floating on black.
 */
export function paintDotField(
  ctx: CanvasRenderingContext2D,
  ox: number,
  oy: number,
  cols: number,
  rows: number,
  pitch: number,
) {
  ctx.fillStyle = 'rgba(91, 54, 8, 0.28)'
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      ctx.beginPath()
      ctx.arc(ox + col * pitch, oy + row * pitch, Math.max(0.45, pitch * 0.24), 0, Math.PI * 2)
      ctx.fill()
    }
  }
}

/**
 * Light the LEDs of a cols×rows grid wherever `mask` has ink. The mask is drawn at
 * `sample`× the grid so a stroke narrower than one LED still lands on the dots it
 * crosses instead of aliasing away. Shared by every sign on the map: one display
 * technology, several pieces of content.
 */
export function stampDotGrid(
  ctx: CanvasRenderingContext2D,
  mask: HTMLCanvasElement,
  cols: number,
  rows: number,
  sample: number,
  ox: number,
  oy: number,
  pitch: number,
  color: string,
  fill = 0.3,
) {
  const pixels = mask
    .getContext('2d', { willReadFrequently: true })!
    .getImageData(0, 0, mask.width, mask.height).data

  ctx.fillStyle = color
  ctx.shadowColor = color
  ctx.shadowBlur = pitch * 0.65
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      let alpha = 0
      for (let sy = 0; sy < sample; sy++) {
        for (let sx = 0; sx < sample; sx++) {
          alpha += pixels[(((row * sample + sy) * mask.width + col * sample + sx) * 4) + 3]
        }
      }
      if (alpha < 255 * sample * sample * 0.18) continue
      ctx.beginPath()
      ctx.arc(ox + col * pitch, oy + row * pitch, Math.max(0.55, pitch * fill), 0, Math.PI * 2)
      ctx.fill()
    }
  }
  ctx.shadowBlur = 0
}

/**
 * Stamp one line of type into a sign mask, filling the width it is given.
 *
 * The rule both fleets follow, and the reason this is a function rather than four
 * copies of three lines: a real sign condenses FIRST and only then gets shorter. Squeeze
 * the whole way and "34 ST HUDSON YARDS" becomes a picket fence; shrink the whole way
 * and a long destination is a two-LED-tall smear while half the display sits empty. So
 * the type is condensed as far as `condense` allows, and anything still over-long comes
 * off both axes together after that.
 *
 * `x`/`width` are the band it must fit inside, in mask pixels; it is centred in that band.
 */
export function stampSignLabel(
  mctx: CanvasRenderingContext2D,
  label: string,
  x: number,
  width: number,
  centreY: number,
  fontPx: number,
  condense: number,
) {
  mctx.font = signFont(fontPx)
  const fit = Math.min(1, width / Math.max(1, mctx.measureText(label).width))
  mctx.save()
  mctx.translate(x + width / 2, centreY)
  mctx.scale(fit, Math.min(1, fit / condense))
  mctx.fillText(label, 0, 0)
  mctx.restore()
}

/**
 * What a destination sign is allowed to show. Real matrices carry upper case and a
 * handful of marks; anything else (an em dash out of a GTFS headsign, an accent) becomes
 * a space rather than a missing-glyph box on an LED display that has no such glyph.
 */
export function signText(text: string): string {
  return text.toUpperCase().replace(/[^A-Z0-9&/ .'-]/g, ' ').replace(/\s+/g, ' ').trim()
}
